import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { DigiResponse } from '@core/models/response/core';
import { PupilAttendanceSchedule, SchoolCalendarException } from '@core/models/domain/school-calendar.domain';
import {
  CancelSchoolCalendarExceptionRequest,
  SchoolCalendarExceptionRequest,
  SetPupilAttendanceScheduleRequest
} from '@core/models/request/school-calendar.request';
import { buildApiEndpoint, handleError } from '@core/utils/service';

/**
 * SchoolCalendarService
 *
 * Backend calls for the School Calendar page: the periods during which the school's restrictions
 * are suspended, and the individual timetables of part-time pupils.
 *
 * <p>Nothing here edits a restriction. An exception is a separate record the backend consults when
 * it decides whether a school restriction is applicable, so recurring schedules survive a holiday
 * untouched. The school is taken from the caller's token by the backend, never sent from here.</p>
 */
@Injectable({
  providedIn: 'root'
})
export class SchoolCalendarService {
  API_URI: string;

  constructor(private http: HttpClient, private apiService: ApiService) {
    this.API_URI = this.apiService.getApiURL('UserService', 'school-account/calendar');
  }

  loadExceptions(from?: string, to?: string): Observable<SchoolCalendarException[]> {
    const query: string[] = [];
    if (from) {
      query.push(`from=${from}`);
    }
    if (to) {
      query.push(`to=${to}`);
    }
    const path = query.length ? `exceptions?${query.join('&')}` : 'exceptions';
    return this.http
      .get<DigiResponse<SchoolCalendarException[]>>(buildApiEndpoint(this.API_URI, path))
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== 'OK') {
            throw response?.statusCode ?? new Error('Loading the school calendar failed');
          }
          return Array.isArray(response.body) ? response.body : [];
        }),
        catchError((error) => handleError<SchoolCalendarException[]>('Loading the school calendar', error))
      );
  }

  /** Creates the exception, or replaces it when the request carries an `exceptionId`. */
  saveException(request: SchoolCalendarExceptionRequest): Observable<void> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, 'exceptions'), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== 'OK') {
            throw response?.statusCode ?? new Error('Saving the calendar entry failed');
          }
        }),
        catchError((error) => handleError<void>('Saving the calendar entry', error))
      );
  }

  cancelException(exceptionId: string): Observable<void> {
    const request: CancelSchoolCalendarExceptionRequest = { exceptionId };
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, 'exceptions/cancel'), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== 'OK') {
            throw response?.statusCode ?? new Error('Withdrawing the calendar entry failed');
          }
        }),
        catchError((error) => handleError<void>('Withdrawing the calendar entry', error))
      );
  }

  loadAttendanceSchedules(): Observable<PupilAttendanceSchedule[]> {
    return this.http
      .get<DigiResponse<PupilAttendanceSchedule[]>>(buildApiEndpoint(this.API_URI, 'attendance-schedules'))
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== 'OK') {
            throw response?.statusCode ?? new Error('Loading pupil timetables failed');
          }
          return Array.isArray(response.body) ? response.body : [];
        }),
        catchError((error) => handleError<PupilAttendanceSchedule[]>('Loading pupil timetables', error))
      );
  }

  setAttendanceSchedule(request: SetPupilAttendanceScheduleRequest): Observable<void> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, 'attendance-schedules'), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== 'OK') {
            throw response?.statusCode ?? new Error('Saving the pupil timetable failed');
          }
        }),
        catchError((error) => handleError<void>('Saving the pupil timetable', error))
      );
  }

  removeAttendanceSchedule(pupilAccountId: string): Observable<void> {
    return this.http
      .post<DigiResponse<string>>(
        buildApiEndpoint(this.API_URI, `attendance-schedules/${encodeURIComponent(pupilAccountId)}/remove`), {})
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== 'OK') {
            throw response?.statusCode ?? new Error('Removing the pupil timetable failed');
          }
        }),
        catchError((error) => handleError<void>('Removing the pupil timetable', error))
      );
  }
}
